<template>
    <ProfileHeaderSectionCommon>
        <template #availability="{mobile}">
            <AvailabilitySection
                v-if="loggedIn"
                class="profile-header-section-read-only__availability"
                :class="{'profile-header-section-read-only__availability--mobile': mobile}"
                :data-testid="`profile-availability-tooltip${mobile ? '-mobile' : ''}`"
                :show-subtitle="false"
            />
        </template>
        <template #wishlist>
            <WishlistIndicator v-if="hasAccessToWishlist" />
        </template>
        <template #first-indicator>
            <PriceIndicator
                v-if="profile"
                :price="profile.price.price"
                :price-hidden="profile.price.priceHidden"
                :currency-code="profile.price.currencyCode"
                :is-strategy-profile="isStrategyProfile"
                :edit-mode="false"
            />
        </template>
        <template #third-indicator>
            <ReviewsRecommendationsIndicator :appraisals="appraisals" :recommendations="recommendations" />
        </template>
        <template #actions>
            <VJoyWrapper class="profile-header-section-read-only__actions" no-margin column-gap="1" row-gap="4" direction="column" justify="center">
                <ProjectProposalOrShortlistChoice />
                <template v-if="!comesFromAdvancedSearch">
                    <ShareProfileDialog />
                </template>
            </VJoyWrapper>
        </template>
    </ProfileHeaderSectionCommon>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useAuth} from '#imports';
    import {VJoyWrapper} from '@maltjoy/core-vue';
    import {useComesFromAdvancedSearch} from '~/composables/useComesFromAdvancedSearch';
    import {useAppraisalStore} from '~/features/appraisals/store/appraisalStore';
    import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
    import ShareProfileDialog from '~/features/share-profile/ui/ShareProfileDialog.vue';
    import ProjectProposalOrShortlistChoice from '../ProjectProposalOrShortlistChoice.vue';
    import ProfileHeaderSectionCommon from './ProfileHeaderSectionCommon.vue';
    import AvailabilitySection from '../availability/AvailabilitySection.vue';
    import PriceIndicator from '../indicators/price/PriceIndicator.vue';
    import ReviewsRecommendationsIndicator from '../indicators/reviews-recommendations/ReviewsRecommendationsIndicator.vue';
    import WishlistIndicator from '../indicators/WishlistIndicator.vue';
    import {useStrategyProfile} from '@/composables/useStrategyProfile';
    import {useProfileStore} from '@/store/profileStore';

    const {appraisals} = storeToRefs(useAppraisalStore());
    const profileStore = useProfileStore();
    const {profile} = storeToRefs(profileStore);

    const {recommendations} = storeToRefs(useRecommendationsStore());
    const isStrategyProfile = computed(() => useStrategyProfile() || false);

    const {loggedIn, currentIdentityIsClient} = storeToRefs(useAuth());
    const route = useRoute();
    const isInOverviewMode = computed<boolean>(() => route?.fullPath?.includes('overview') || false);
    const hasAccessToWishlist = computed(() => loggedIn.value && currentIdentityIsClient.value && !isInOverviewMode.value);
    const comesFromAdvancedSearch = useComesFromAdvancedSearch();
</script>

<style scoped lang="scss">
    .profile-header-section-read-only {
        &__availability {
            position: absolute;
            bottom: var(--joy-core-spacing-2);
            left: var(--joy-core-spacing-2);
            max-width: calc(100% - var(--joy-core-spacing-4));

            &--mobile {
                display: none;
                position: relative;
                bottom: unset;
                left: unset;
                max-width: unset;
            }

            @container profile-header-section (max-width: 750px) {
                display: none;

                &--mobile {
                    display: block;
                }
            }
        }

        &__actions {
            align-self: stretch;
        }
    }
</style>
